import { classDefaults, defaultQuestHero, questTiers, selectDefaultGear } from "@/data/quest";
import {
  MissionCompletePageState,
  QuestMissionState,
  QuestWave,
  QuestWaveType
} from "@/data/redux/questSlice";
import {
  GainType,
  IArmyData,
  IQuestSkill,
  IQuestUnit,
  ISelectedUnit,
  ISpecialRule,
  IUpgradeGains,
  IUpgradeGainsItem,
  IUpgradeGainsWeapon,
  IUpgradeOption,
  IUpgradePackage,
  IUpgradeSection
} from "@/data/types";

import { FEATURE_FLAGS } from "@/data/globals";
import { isFeatureEnabled } from "@/env";
import { PayloadAction, createAction, createReducer, nanoid } from "@reduxjs/toolkit";
import { groupBy, last, maxBy, orderBy, shuffle, sortBy, sumBy } from "lodash-es";
import CalcService from "./calc/calcService";
import { calculateUnitTotal, isHero, makeCopy } from "./Helpers";
import UnitService from "./UnitService";
import UpgradeService from "./UpgradeService";

const ENABLE_V36 = isFeatureEnabled(FEATURE_FLAGS.V3_6);
const enableV2Waves = isFeatureEnabled(FEATURE_FLAGS.QUEST_WAVES_V2);

export enum CombatStatChoice {
  TwoSkills,
  OneSkillOneEnd,
  TwoEnd,
  OneQua,
  OneCaster,
  OneSkill,
  OneEnd,
  TwoTough,
  MinusQuaTwoSkillsTwoPow
}

export type QuestHeroBase = {
  id: string;
  name: string;
  rules: ISpecialRule[];
  sq: boolean;
  equipment: IUpgradeGainsWeapon[];
  quality: number;
  defense: number;
  tough: number;
  end: number;
  str: number;
  dex: number;
  wil: number;
  cost: number;
  feats: string[];
  cloud: boolean;
};

export type QuestHeroSelections = QuestHeroBase & {
  selections: {
    baseHero: {
      id: string;
      name: string;
    };
    faction: {
      id: string;
      name: string;
    };
    class: {
      id: string;
      name: string;
    };
  };
  tracking: {
    stress: number;
    wounds: number;
    conditions: string[];
    gold: number;
  };
  actions?: PayloadAction<any>[];
  playerId?: string;
  saveVersion?: number;
};

export type QuestHeroDisplay = QuestHeroBase & {
  skillSlots: number;
  skills: string[];
  loadout: IUpgradeGains[];
  consumables: { name: string; count: number }[];
  selectedUpgrades: { instanceId: string; upgrade: IUpgradeSection; option: IUpgradeOption }[];
  factionName: string;
  baseHeroName: string;
  class: string;
  xp: number;
  pts: number;
  size: number;
  level: number;
  pendingLevel: number;
  baseTough: number;
  goldStash: number;
  tier: number;
  injuries: { name: string; duration: number }[];
  playerId?: string;
  tracking: {
    stress: number;
    wounds: number;
    conditions: string[];
  };
  saveVersion?: number;
  heroCost: number;
  costBreakdown?: {
    endValue: number;
    endMultiplier: number;
    endCost: number;
    skillsValue: number;
    skillsMultiplier: number;
    skillsCost: number;
    abilityValue: number;
    abilityMultiplier: number;
    abilityCost: number;
  };
};

//#region Hero Actions

export type HeroUpgrade = { instanceId: string; optionId: string; sectionId: string };

export const heroBuyUpgrade = createAction<HeroUpgrade>("hero/buyUpgrade");
export const heroFindUpgrade = createAction<HeroUpgrade>("hero/findUpgrade");
export const heroSellUpgrade = createAction<Omit<HeroUpgrade, "instanceId">>("hero/sellUpgrade");
export const heroRemoveUpgrade = createAction<{ instanceId: string }>("hero/removeUpgrade");
export const heroGainSkill = createAction<string>("hero/gainSkill");
export const heroGainAbility = createAction<"str" | "dex" | "wil">("hero/gainAbility");
export const heroGainCombatStat = createAction<CombatStatChoice>("hero/gainCombatStat");
export const heroGainXP = createAction<number>("hero/gainXP");
export const heroGainGold = createAction<number>("hero/gainGold");
export const heroGainLevel = createAction<number>("hero/gainLevel");
export const heroGainTough = createAction<number>("hero/gainTough");
export const heroGainInjury = createAction<string>("hero/gainInjury");
export const heroHealInjury = createAction<string>("hero/healInjury");
export const heroCompleteMission = createAction("hero/heroCompleteMission");

//#endregion

const abilityChoices = [heroGainAbility("str"), heroGainAbility("dex"), heroGainAbility("wil")];

export const questHeroLevelXpThresholds = ENABLE_V36
  ? {
      0: null,
      3: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneSkill)],
      6: abilityChoices,
      12: [heroGainTough(2), heroGainCombatStat(CombatStatChoice.OneQua)],
      18: [
        heroGainCombatStat(CombatStatChoice.TwoEnd),
        heroGainCombatStat(CombatStatChoice.TwoSkills),
        heroGainCombatStat(CombatStatChoice.OneCaster)
      ],
      27: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneEnd)],
      36: abilityChoices,
      48: [heroGainTough(2), heroGainCombatStat(CombatStatChoice.TwoEnd)],
      60: [
        heroGainCombatStat(CombatStatChoice.TwoEnd),
        heroGainCombatStat(CombatStatChoice.TwoSkills),
        heroGainCombatStat(CombatStatChoice.OneCaster)
      ],
      75: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneSkill)],
      90: abilityChoices,
      108: [heroGainTough(2), heroGainCombatStat(CombatStatChoice.OneQua)],
      126: [
        heroGainCombatStat(CombatStatChoice.TwoEnd),
        heroGainCombatStat(CombatStatChoice.TwoSkills),
        heroGainCombatStat(CombatStatChoice.OneCaster)
      ],
      147: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneEnd)],
      168: abilityChoices
    }
  : {
      0: null,
      2: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneSkill)],
      4: abilityChoices,
      8: [heroGainTough(2), heroGainCombatStat(CombatStatChoice.OneQua)],
      12: [
        heroGainCombatStat(CombatStatChoice.TwoEnd),
        heroGainCombatStat(CombatStatChoice.TwoSkills),
        heroGainCombatStat(CombatStatChoice.OneCaster)
      ],
      18: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneEnd)],
      24: abilityChoices,
      32: [heroGainTough(2), heroGainCombatStat(CombatStatChoice.TwoEnd)],
      40: [
        heroGainCombatStat(CombatStatChoice.TwoEnd),
        heroGainCombatStat(CombatStatChoice.TwoSkills),
        heroGainCombatStat(CombatStatChoice.OneCaster)
      ],
      50: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneSkill)],
      60: abilityChoices,
      82: [heroGainTough(2), heroGainCombatStat(CombatStatChoice.OneQua)],
      94: [
        heroGainCombatStat(CombatStatChoice.TwoEnd),
        heroGainCombatStat(CombatStatChoice.TwoSkills),
        heroGainCombatStat(CombatStatChoice.OneCaster)
      ],
      108: [heroGainTough(1), heroGainCombatStat(CombatStatChoice.OneEnd)],
      122: abilityChoices
    };

export const maxHeroXP = parseInt(last(Object.keys(questHeroLevelXpThresholds)));

const shopHeroIds = {
  t3: "ecrw-Zr",
  t6: "6MHP4Q-"
};

const baseHeroId = (hero: { selections: { baseHero: { id: string } } }) => hero.selections.baseHero.id;
const shopHeroId = (displayHero: { baseTough: number }) => shopHeroIds["t" + displayHero.baseTough];
export const getGoldCostSelector =
  (hero: { selections: { baseHero: { id: string } } }, displayHero: { baseTough: number }) =>
  (x: { unitId: string }) =>
    x.unitId === shopHeroId(displayHero) || x.unitId === baseHeroId(hero);

export const questStartingGold = 30;
export const currencySuffix = (sq: boolean) => (sq ? "c" : "g");
export const currencyName = (sq: boolean) => (sq ? "Credits" : "Gold");

const gainTough = (hero: QuestHeroDisplay, value: number) => {
  hero.tough += value;
  const toughRule = hero.rules.find((x) => x.name === "Tough");
  if (toughRule) toughRule.rating = parseInt(toughRule.rating as any) + value;
};

const applyCombatStat = (hero: QuestHeroDisplay, action: PayloadAction<CombatStatChoice>) => {
  switch (action.payload) {
    case CombatStatChoice.TwoSkills:
      hero.skillSlots += 2;
      break;
    case CombatStatChoice.OneSkill:
      hero.skillSlots += 1;
      break;
    case CombatStatChoice.OneSkillOneEnd:
      hero.skillSlots += 1;
      hero.end += 1;
      break;
    case CombatStatChoice.OneEnd:
      hero.end += 1;
      break;
    case CombatStatChoice.TwoEnd:
      hero.end += 2;
      break;
    case CombatStatChoice.OneQua:
      hero.quality -= 1;
      break;
    case CombatStatChoice.TwoTough:
      gainTough(hero, 2);
      break;
    case CombatStatChoice.MinusQuaTwoSkillsTwoPow:
      hero.quality += 1;
      hero.skillSlots += 2;
      hero.end += 2;
      break;
    case CombatStatChoice.OneCaster:
      if (!hero.rules) break; // applyCombatStats sometimes called without base hero
      const casterRule = hero.rules
        .concat(UnitService.getAllUpgradedRules(hero))
        .find((x) => x.name === "Caster");
      if (casterRule) {
        casterRule.rating = parseInt(casterRule.rating as any) + 1;
        (casterRule as any).label = `Caster(${casterRule.rating})`;
      } else {
        hero.rules.push({
          name: "Caster",
          rating: 1
        });
      }
      break;
  }
};

export const applyCombatStats = (hero: QuestHeroSelections) => {
  for (const action of hero.actions ?? []) {
    if (heroGainCombatStat.match(action)) {
      applyCombatStat(hero as any, action);
    }
  }
  return hero;
};

export const getDefaultHero = (
  heroSelections: QuestHeroSelections
): QuestHeroDisplay & QuestHeroSelections => {
  const { selections } = heroSelections;
  const hero = {
    ...makeCopy(heroSelections),
    baseHeroName: selections.baseHero.name,
    factionName: selections.faction.name,
    equipment: [],
    rules: [],
    class: selections.class.name,
    quality: 5,
    defense: 6,
    str: 6,
    dex: 6,
    wil: 6,
    end: 3,
    loadout: [],
    selectedUpgrades: [],
    skillSlots: 0,
    skills: [],
    xp: 0,
    size: 1,
    level: 1,
    goldStash: ENABLE_V36 ? 0 : questStartingGold,
    injuries: [],
    playerId: heroSelections.playerId,
    tracking: heroSelections.tracking,
    saveVersion: heroSelections.saveVersion,
    consumables: [],
    pts: 0,
    pendingLevel: 0,
    baseTough: 0,
    tier: 0,
    heroCost: 0
  };
  return hero;
};

export const buildHero = (
  heroSelections: QuestHeroSelections,
  heroDatum: {
    book: IArmyData;
    shop: IUpgradePackage[];
    heroCost: number;
    questCalcValues: Record<string, any>;
  },
  skills: { id: string; name: string }[]
): QuestHeroDisplay => {
  const { actions } = heroSelections;
  const { book, shop, heroCost } = heroDatum;

  let hero: QuestHeroDisplay = getDefaultHero(heroSelections);

  const baseHero = book.units.find(
    (x) => x.id === heroSelections.selections.baseHero.id // || x.id === heroSelections.id
  );
  const upgradeSections = getSectionsForHero(heroSelections, book, shop);

  if (baseHero) {
    hero.equipment = makeCopy(baseHero.weapons) as IUpgradeGainsWeapon[];
    hero.rules = baseHero.rules.filter((x) => x.name !== "Hero");
    hero.tough = parseInt(baseHero.rules.find((x) => x.name === "Tough").rating as any);
    hero.baseTough = hero.tough / 3;
    const builtHero = UpgradeService.buildUpgrades({
      ...hero,
      weapons: hero.equipment,
      items: baseHero.items
    });
    hero = {
      ...builtHero,
      equipment: builtHero.weapons
    };
  }

  //#region Actions

  const gainUpgrade = (
    upg: { instanceId: string; sectionId: string; optionId: string },
    callback: (section, option) => void
  ) => {
    const section = upgradeSections.find((sec) => sec.uid === upg.sectionId);
    const option = section?.options.find((opt) => opt.uid === upg.optionId);
    if (option) {
      try {
        callback(section, option);
      } catch {}
    } else {
      //console.error("Cannot find quest hero upgrade...", upg);
      //debugger;
    }
  };

  const heroReducer = createReducer(hero, (builder) =>
    builder
      .addCase(heroGainCombatStat, (hero, action) => {
        applyCombatStat(hero, action);
      })
      .addCase(heroGainSkill, (hero, action) => {
        const migrate = (payload: string) => {
          if (payload === "Dread Shot") {
            return "4yazucezkW-w_qT2IHbGG";
          }
          return payload;
        };
        const skillId = migrate(action.payload);
        const skill = skills.find((x) => x.id === skillId || x.name === skillId);
        hero.skills.push(skill.name);
      })
      .addCase(heroGainAbility, (hero, action) => {
        hero[action.payload]--;
      })
      .addCase(heroBuyUpgrade, (hero, action) => {
        gainUpgrade(action.payload, (section, option) => {
          const cost = option.costs.find(getGoldCostSelector(heroSelections, hero));
          const goldCost = cost?.questCost ?? cost?.cost ?? 999;
          hero.goldStash -= goldCost;
          UpgradeService.apply(hero as any, section, option, action.payload.instanceId);
        });
      })
      .addCase(heroFindUpgrade, (hero, action) => {
        gainUpgrade(action.payload, (section, option) => {
          UpgradeService.apply(hero as any, section, option, action.payload.instanceId);
        });
      })
      .addCase(heroSellUpgrade, (hero, action) => {
        const { sectionId, optionId } = action.payload;
        const section = upgradeSections.find((sec) => sec.uid === sectionId);
        const option = section?.options.find((opt) => opt.uid === optionId);
        const cost = option.costs.find(getGoldCostSelector(heroSelections, hero));
        const goldCost = cost?.questCost ?? cost?.cost ?? 999;
        hero.goldStash += goldCost;
        const removeIndex = hero.selectedUpgrades.findLastIndex(
          (x) => x.option.uid === optionId && x.upgrade.uid === sectionId
        );
        hero.selectedUpgrades.splice(removeIndex, 1);
      })
      .addCase(heroRemoveUpgrade, (hero, action) => {
        const { instanceId } = action.payload;
        const index = hero.selectedUpgrades.findIndex((x) => x.instanceId === instanceId);
        hero.selectedUpgrades.splice(index, 1);
      })
      .addCase(heroGainXP, (hero, action) => {
        hero.xp += action.payload;
      })
      .addCase(heroGainGold, (hero, action) => {
        hero.goldStash += action.payload;
        hero.goldStash = Math.min(hero.goldStash, 1000);
      })
      .addCase(heroGainLevel, (hero, action) => {
        hero.level += action.payload || 1;
      })
      .addCase(heroGainTough, (hero, action) => {
        gainTough(hero, action.payload);
      })
      .addCase(heroGainInjury, (hero, action) => {
        hero.injuries.push({ name: action.payload, duration: 3 });
      })
      .addCase(heroHealInjury, (hero, action) => {
        const index = hero.injuries.findIndex((x) => x.name === action.payload);
        if (index >= 0) {
          hero.injuries.splice(index, 1);
        }
      })
      .addCase(heroCompleteMission, (hero) => {
        for (let i = hero.injuries.length - 1; i >= 0; i--) {
          hero.injuries[i].duration--;
          if (hero.injuries[i].duration <= 0) {
            hero.injuries.splice(i, 1);
          }
        }
      })
  );

  for (const action of actions ?? []) {
    hero = heroReducer(hero, action);
  }

  //#endregion

  //#region Consumables

  const consumableRules = UnitService.getUpgradeRules(hero as any).filter((x: any) => x.isConsumable);
  const consumableGroups = groupBy(consumableRules, "name");
  const consumables = Object.keys(consumableGroups)
    .map((name) => ({
      name,
      count: consumableGroups[name].length
    }))
    .filter((x) => x.count > 0);

  //#endregion

  //#region Cost

  let cost = heroCost ?? 0;
  const { questCalcValues } = heroDatum;
  // Endurance cost
  const endMultiplier = ENABLE_V36 ? questCalcValues["PowerCost"] : 5;
  const endValue = hero.end - (ENABLE_V36 ? 0 : defaultQuestHero(false).end); // v3.6 pay for all end
  const endCost = endValue * endMultiplier;
  cost += endCost;

  // Skills
  // const skillsMultiplier = ENABLE_V36 ? questCalcValues["SkillCost"] : 5;
  // const skillsValue = Math.max(hero.skillSlots, hero.skills.length) + (ENABLE_V36 ? 1 : 0); // v3.6 pay for starting Feat
  // const skillsCost = skillsValue * skillsMultiplier;
  const skillsMultiplier = 5;
  const skillsValue = Math.max(hero.skillSlots, hero.skills.length);
  const skillsCost = skillsValue * skillsMultiplier;
  cost += ENABLE_V36
    ? 0 // Skills have cost formula now, so we're not using this
    : skillsCost;

  const abilityTotal = Math.min(hero.str + hero.dex + hero.wil, 16); // Always have at least 2 ability points
  const abilityPoints = 21 - abilityTotal;

  const abilityMultiplier = ENABLE_V36 ? questCalcValues["AbilityCost"] : 2.5;
  const abilityCost = abilityPoints * abilityMultiplier;
  cost += abilityCost;

  let upgradesCost = 0;

  for (const option of hero.selectedUpgrades.map((su) => su.option)) {
    if (option.gains.some((x: any) => x.isConsumable)) continue;

    const optionPtsCost = option.costs?.find((x) => x.unitId === hero.id)?.cost;
    if (typeof optionPtsCost !== "undefined") {
      upgradesCost += optionPtsCost;
    } else {
      //console.error("Cannot find pts cost for ", option.label);
    }
  }

  cost += upgradesCost;

  //#endregion

  //#region Level

  const pendingLevel = Object.keys(questHeroLevelXpThresholds)
    .map((x) => parseInt(x))
    .reduce((lvl, next) => lvl + (hero.xp >= next ? 1 : 0), 0);

  const tier = (() => {
    for (const tier of questTiers) {
      if (hero.level >= tier.level) {
        return tier.tier;
      }
    }

    return 0;
  })();

  //#endregion

  return Object.freeze({
    ...hero,
    heroCost,
    costBreakdown: {
      endValue,
      endMultiplier,
      endCost,
      skillsValue,
      skillsMultiplier,
      skillsCost,
      abilityValue: abilityPoints,
      abilityMultiplier,
      abilityCost
    },
    cost: CalcService.round(cost),
    tough: UnitService.getTough(hero),
    consumables,
    pendingLevel,
    tier
  }) as QuestHeroDisplay;
};

export const resetHeroForHorde = (heroSelections: QuestHeroSelections) => {
  const resetHero = makeCopy(heroSelections);
  if (!resetHero.actions) resetHero.actions = [];

  // Remove all actions after the first gainXP or completeMission action
  const fromIndex = resetHero.actions.findIndex((a) => heroGainXP.match(a) || heroCompleteMission.match(a));
  if (fromIndex > 0) {
    resetHero.actions = resetHero.actions.slice(0, fromIndex);
  }
  resetHero.tracking = {
    gold: 0,
    wounds: 0,
    stress: 0,
    conditions: []
  };
  return resetHero;
};

const RANGER_CLASS_ID = "R0WM1rvOqqVV";

export const getClassDefaultActions = (
  classId: string,
  baseHeroId: string,
  heroTough: number,
  sections: IUpgradeSection[],
  skills: IQuestSkill[]
) => {
  const defaults = classDefaults.find((x) => x.classId === classId);
  const gearOptions = selectDefaultGear(baseHeroId, sections, heroTough, classId === RANGER_CLASS_ID);

  const goldCostSelector = getGoldCostSelector(
    { selections: { baseHero: { id: baseHeroId } } },
    { baseTough: heroTough }
  );
  const startingGold = sumBy(gearOptions, (x) => x.costs.find(goldCostSelector)?.questCost);

  const actions = [];
  if (ENABLE_V36) actions.push(heroGainGold(startingGold));

  actions.push(
    ...defaults.abilityChoices.map((x: any) => heroGainAbility(x)),
    ...defaults.combatStatChoices.map((x) => heroGainCombatStat(CombatStatChoice[x])),
    ...defaults.skillChoices.map((x) => {
      const skillDef = skills.find((s) => s.id === x);
      return heroGainSkill(skillDef.id);
    }),
    ...gearOptions.map((opt) =>
      heroBuyUpgrade({
        instanceId: nanoid(9),
        sectionId: opt.parentSectionId,
        optionId: opt.uid
      })
    )
  );

  return actions;
};

export function getSectionsForHero(hero: QuestHeroSelections, book: IArmyData, shop: IUpgradePackage[]) {
  const baseHero = book.units.find((x) => x.id === hero.selections.baseHero.id) as IQuestUnit;
  if (!baseHero) return [];
  const upgradeSections: IUpgradeSection[] = []; //makeCopy(book.upgradePackages.flatMap((x) => x.sections)) ?? [];

  const bookPackages = makeCopy(book.upgradePackages);
  const shopClone = makeCopy(shop);

  // Merge packages
  for (const shopPkg of shopClone) {
    const matchingBookPackage = bookPackages.find((x) => x.hint === shopPkg.hint);
    if (matchingBookPackage) {
      for (const section of shopPkg.sections) {
        const matchingBookSection = matchingBookPackage.sections.find((x) => x.label === section.label);
        if (matchingBookSection) {
          matchingBookSection.options.push(...section.options);
          matchingBookSection.options = sortBy(
            matchingBookSection.options,
            (x) => maxBy(x.costs, "cost").cost
          );
        } else {
          matchingBookPackage.sections.push(section);
        }
      }
      (shopPkg as any).ignore = true;
    }
  }

  for (const x of bookPackages.concat(shopClone).filter((x: any) => !x.ignore)) {
    for (const section of x.sections) {
      section.options = section.options.filter((option) => {
        for (const gain of option.gains) {
          const isBanned = (x) =>
            x.type === GainType.Rule &&
            (x.name === "Caster" || x.name === "Spawn" || x.name === "Summon" || x.name.endsWith("Guard"));
          if (isBanned(gain)) return false;
          if (gain.type === GainType.Item) {
            if ((gain as IUpgradeGainsItem).content.some((x) => isBanned(x))) {
              return false;
            }
          }
        }
        return true;
      });
    }

    if (!x.hint.startsWith("!")) {
      upgradeSections.push(...x.sections);
      continue;
    }

    // Package for specific Classes only
    if (x.hint.startsWith("!Class")) {
      const classFilter = /!Class: (.+)/.exec(x.hint)?.[1];
      const classes = classFilter.split(",").map((x) => x.trim());
      if (classes.includes(hero.selections.class.name)) {
        upgradeSections.push(...x.sections);
      }
    }

    // Package for heroes that start with no access to Caster
    if (x.hint.startsWith("!Caster")) {
      let hasCaster = hero.rules.some((x) => x.name === "Caster");
      // Return true if hero has no available caster upgrades
      for (const section of upgradeSections) {
        const gains = section.options.flatMap((x) => x.gains);
        for (const gain of gains) {
          const isMatch = (x) => x.type === GainType.Rule && x.name === "Caster";
          if (isMatch(gain)) {
            hasCaster = true;
          }
          if (gain.type === GainType.Item) {
            if ((gain as IUpgradeGainsItem).content.some((x) => isMatch(x))) {
              hasCaster = true;
            }
          }
        }
      }

      if (!hasCaster) {
        // Add option
        const options = x.sections.flatMap((x) => x.options);
        const heroSection = upgradeSections.find((x) => x.isHeroUpgrade);
        if (heroSection) {
          heroSection.options.push(...options);
        } else {
          upgradeSections.push(...x.sections);
        }
      }
    }
  }

  return upgradeSections;
}

export const getHeroValidationErrors = (
  heroSelections: QuestHeroSelections,
  displayHero: QuestHeroDisplay,
  sq: boolean
) => {
  const skillChoices = heroSelections.actions.filter((x) => x.type === heroGainSkill.type);
  const abilityChoices = heroSelections.actions.filter((x) => x.type === heroGainAbility.type);
  const combatStatChoices = heroSelections.actions.filter((x) => x.type === heroGainCombatStat.type);
  const validAbilityPoints = abilityChoices.length >= 2;
  const validChoices = combatStatChoices.length >= 2;
  const validSkills = skillChoices.length === displayHero?.skillSlots;

  return [
    !heroSelections.name && "Hero must have a name",
    !heroSelections.selections.baseHero?.id && `Must select a Hero`,
    !heroSelections.selections.class && `Must select a Hero class`,
    !validAbilityPoints && "Both ability stat points must be assigned",
    !validChoices && "Both combat stat choices must be selected",
    !validSkills && "All skill choices must be selected",
    displayHero?.goldStash < 0 && `${currencyName(sq)} Stash cannot be negative`
  ].filter((x) => Boolean(x));
};

export const getHeroTitle = (hero: QuestHeroDisplay, displayLevel?: number) => {
  const isNarrative = hero.rules.some((r) => r.name === "Unique");
  if (isNarrative) return "";
  const level = displayLevel ?? hero.level;
  return level >= 15 ? "Legendary " : level <= 8 ? "Aspiring " : "";
};

//#region Wave Generation

export const globalDifficultyMultiplier = 0.25;
export const getDifficultyMultiplier = (hordeMode: boolean, waveNumber: number, difficulty: number) => {
  if (ENABLE_V36) {
    if (hordeMode) return waveNumber * globalDifficultyMultiplier;
    else return globalDifficultyMultiplier * 2 ** (difficulty - 1);
  } else {
    return (hordeMode ? waveNumber : difficulty) * globalDifficultyMultiplier;
  }
};

export const generateWave = (
  type: QuestWaveType,
  mission: QuestMissionState,
  heroCost: number,
  units: ISelectedUnit[],
  addWave: (wave: Omit<QuestWave, "id">) => void,
  customWaveCost: number,
  bossWave: boolean = false
) => {
  const { hordeMode, difficulty, waveNumber } = mission;
  const unitsAlreadyInWave = mission.waves.flatMap((x) => x.units.map((u) => u.selectionId));
  const available = units.filter((x) => !unitsAlreadyInWave.includes(x.selectionId));
  const difficultyMultiplier = getDifficultyMultiplier(hordeMode, waveNumber, difficulty);
  const wavePointTotal = customWaveCost || difficultyMultiplier * heroCost;

  const lastWave = keyFromWave(mission.lastWave);

  let wave = generateWaveUnits(shuffle(available), wavePointTotal, difficulty, lastWave);

  const regenerateThreshold = wavePointTotal - 30;
  // If the wave is <= 30pt below the total then try again with a higher limit
  if (wave.cost <= regenerateThreshold) {
    //wave = getWave(wavePointTotal + 25);
    wave = generateWaveUnits(shuffle(available), wavePointTotal + 25, difficulty, lastWave, bossWave);
  }

  if (wave.units.length > 0) {
    addWave({ type, ...wave });
  } else {
    addWave(null);
  }
};

/**
generate X waves and spawn closest to limit
 - don't spawn a wave identical to the previous one, if possible
Step 1) 33% chance to get one T3+ (Quest T9+) unit, if you got one, then 33% chance it's a villain
- difficulty 1 = can't spawn T3+ (Quest T9+)
- difficulty 2 = can't spawn T9+ (Quest T27+)
- difficulty 3 = can't spawn T15+ (Quest T45+)
- difficulty 4 = anything goes
Step 2) add at least one size [3] unit
Step 3) fill whatever remaining with random non-T3+ (Quest T9) units
Step 4) fill whatever remaining with any remaining available units

- generate X waves and spawn closest to limit
 - don't generate a wave identical to the previous one, if possible
- add one TX+ unit, 50% chance it's a villain
  - difficulty 1 = must be T3+ (otherwise go down by 3 and repeat)
  - difficulty 2 = must be T9+ (otherwise go down by 3 and repeat)
  - difficulty 3 = must be T15+ (otherwise go down by 3 and repeat)
  - difficulty 4 = anything goes
- add one size [3] unit
- fill remaining points with non-T3+ units (if no other option, include T3+)
- note: alternate between Tough(3) unit and size [3] units (if no other option, ignore)

add one TX+ unit, 50% chance it's a villain

while wave cost is below limit:
  add one size [3] unit
  add a Tough(3) unit

fill remaining points with anything that's left

 */
const keyFromWave = (wave) =>
  wave?.units
    .map((x) => x.name)
    .sort()
    .join();
const toughLimits = { 1: 9, 2: 27, 3: 45, 4: 99999 };
const tough = UnitService.getTough;

const generateWaveUnits = (
  units: ISelectedUnit[],
  wavePointTotal: number,
  difficulty: number,
  lastWaveCompare: string,
  bossWave: boolean = false,
  swarmMode: boolean = false
) => {
  // filter available units by tough limit for this difficulty
  const availableUnits = units
    .map((x) => ({ ...x, cost: calculateUnitTotal(x) }))
    .filter((x) => tough(x) < toughLimits[difficulty]);

  const generate = () => {
    const waveUnits = [];
    let waveCost = 0;

    const addUnits = (filter: (x: ISelectedUnit) => boolean, max?: number, count?: number) => {
      // If nothing left to add then do nothing
      if (max <= 0) return;

      // Shuffle the available units into a random order
      const selectionPool = shuffle(
        availableUnits.filter((x) => !waveUnits.some((wu) => wu.selectionId === x.selectionId))
      ).filter(filter);

      let currentCost = 0;
      const results = [];
      // For each available unit in the pool...
      for (const unit of selectionPool) {
        const canFitUnitIntoWave = currentCost + unit.cost <= max;
        const spaceLeftInBatch = results.length < count;
        if ((!max || canFitUnitIntoWave) && (!count || spaceLeftInBatch)) {
          currentCost += unit.cost;
          waveCost += unit.cost;
          results.push(unit);
        }
      }
      waveUnits.push(...results);
    };

    const takeToughUnit = bossWave ? true : Math.random() < 1 / 3;
    const takeVillain = Math.random() <= 1 / (bossWave ? 2 : 3);

    // 33% chance to get one T3+ unit, if you got one, then 33% chance it's a villain
    // If it's a boss wave, always take a tough unit and then 50% chance it's a villain
    if (takeToughUnit) {
      if (takeVillain) {
        addUnits((x) => tough(x) >= 9 && isHero(x), wavePointTotal, 1);
      }
      // If we're not taking a villain, or we _could_ not take one
      if (waveUnits.length === 0) {
        addUnits((x) => tough(x) >= 9, wavePointTotal, 1);
      }
    }

    // add at least 1 size [3] unit
    addUnits((x) => x.size === 3, wavePointTotal - waveCost, 1);

    // fill whatever remaining with random non-T3+ (T9 post questify) units
    addUnits((x) => tough(x) < 9, wavePointTotal - waveCost);

    // fill whatever remaining with random available units
    addUnits(() => true, wavePointTotal - waveCost);

    const sortedUnits = waveUnits.sort((a, b) => {
      if (isHero(a) && !isHero(b)) return -1;
      if (!isHero(a) && isHero(b)) return 1;
      return a.cost - b.cost;
    });

    return {
      units: sortedUnits,
      cost: waveCost
    };
  };

  const generateV2 = () => {
    const waveUnits = [];
    let waveCost = 0;

    /**
    1 = 1/3 chance to get one T3+ unit, if you got one, then 1/3 chance it's a villain
      (only the first stime step 1 is run) if it's a boss wave, instead of 1/3 chance you always take one T3+ unit, with 1/2 chance it's a villain
    2 = add at least one random non-T3+
      if swarm mode, add unit add at least one size [3-5] unit
    3 = add one random non-T3+ unit
      if swarm mode, this includes size [3-5] units
    4 = loop back to step 1
    */

    const addUnit = (filter: (x: ISelectedUnit) => boolean, max?: number) => {
      // If nothing left to add then do nothing
      if (max <= 0) return false;

      // Shuffle the available units into a random order
      const selectionPool = shuffle(
        availableUnits.filter((x) => !waveUnits.some((wu) => wu.selectionId === x.selectionId))
      ).filter(filter);

      // For each available unit in the pool...
      for (const unit of selectionPool) {
        const canFitUnitIntoWave = waveCost + unit.cost <= max;
        if (!max || canFitUnitIntoWave) {
          waveCost += unit.cost;
          waveUnits.push(unit);
          return true;
        }
      }
      return false;
    };

    let i = 0;
    while (true) {
      const isFirstIteration = i++ === 0;
      let takeToughUnit = false;
      let toughUnitIsVillain = false;
      const waveUnitCount = waveUnits.length;

      // (only the first stime step 1 is run) if it's a boss wave, instead of 1/3 chance you always take one T3+ unit, with 1/2 chance it's a villain
      if (isFirstIteration && bossWave) {
        takeToughUnit = true;
        toughUnitIsVillain = Math.random() < 0.5;
      }
      // 1 = 1/3 chance to get one T3+ unit, if you got one, then 1/3 chance it's a villain
      else {
        takeToughUnit = Math.random() < 1 / 3;
        toughUnitIsVillain = Math.random() <= 1 / 3;
      }

      const villainAdded =
        takeToughUnit && toughUnitIsVillain
          ? addUnit((x) => tough(x) >= 9 && isHero(x), wavePointTotal)
          : false;

      if (takeToughUnit && !villainAdded) {
        addUnit((x) => tough(x) >= 9, wavePointTotal);
      }

      // 2 = add one random non-T3+ unit
      //   if swarm mode, add one size [3-5] unit
      if (swarmMode) {
        addUnit((x) => tough(x) < 9 && x.size > 1, wavePointTotal);
      } else {
        addUnit((x) => tough(x) < 9, wavePointTotal);
      }

      // 3 = add one random non-T3+ unit
      //   if swarm mode, this includes size [3-5] units
      addUnit((x) => tough(x) < 9, wavePointTotal);

      // If nothing was added, then break
      if (waveUnitCount === waveUnits.length) {
        break;
      }
    }

    const sortedUnits = waveUnits.sort((a, b) => {
      if (isHero(a) && !isHero(b)) return -1;
      if (!isHero(a) && isHero(b)) return 1;
      return a.cost - b.cost;
    });

    return {
      units: sortedUnits,
      cost: waveCost
    };
  };

  const waves = Array.from({ length: 4 }).map(() => (enableV2Waves ? generateV2() : generate()));
  const withoutDuplicates = waves.filter((wave) => keyFromWave(wave) !== lastWaveCompare);

  return orderBy(withoutDuplicates.length > 0 ? withoutDuplicates : waves, "cost", "desc")[0];
};

//#endregion

export const combatStatName = (choice: CombatStatChoice) => {
  switch (choice) {
    case CombatStatChoice.OneCaster:
      return "Caster(1)";
    case CombatStatChoice.OneQua:
      return "Qua +1";
    case CombatStatChoice.OneEnd:
      return "Pow +1";
    case CombatStatChoice.OneSkill:
      return "1 Skill";
    case CombatStatChoice.OneSkillOneEnd:
      return "1 Skill, Pow +1";
    case CombatStatChoice.TwoEnd:
      return "Pow +2";
    case CombatStatChoice.TwoSkills:
      return "2 Skills";
    case CombatStatChoice.TwoTough:
      return "Tough(+2)";
    case CombatStatChoice.MinusQuaTwoSkillsTwoPow:
      return "Qua -1, +2 Skills, Pow +2";
  }
  return "None";
};

export const applyMissionCompleteState = (page: MissionCompletePageState, hero: QuestHeroSelections) => {
  // Shop actions will mutate the state so it needs replacing before anything else happens
  if (page.state.shopActions) {
    hero.actions = page.state.shopActions;
  }
  // If Shop actions were taken, XP and Gold actions will already have been added
  else {
    if (page.state.xpGain > 0) {
      hero.actions.push(heroGainXP(page.state.xpGain));
    }
    if (page.state.goldGain > 0) {
      hero.actions.push(heroGainGold(page.state.goldGain));
    }
  }

  for (const choice of page.levelChoices) {
    hero.actions.push(...choice.actions);
  }
};
